import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperCore } from "swiper/types";

import { SwiperNavButton } from "@/components/Product/ImageViewer/partials/SwiperNavButton/swiperNavButton.component";
import { BreakpointMinSizes } from "@/services/breakpoint.service";
import {
    getEffectiveLayoutSettings,
    LayoutSettings,
    mapSsrLayoutClasses
} from "@/utils/swiper";

import "./categorySlider.scss";

export type Category = {
    path: string;
    altText: string;
    link: string;
    isTargetBlank: boolean;
};

export type CategorySliderProps = {
    categories: Array<Category>;
};

const CategorySlider = ({ categories = [] }: CategorySliderProps) => {
    const [allowPrevious, setAllowPrevious] = useState(false);
    const [allowNext, setAllowNext] = useState(true);
    const [mainSwiper, setMainSwiper] = useState<SwiperCore>();
    const navigationPrevRef = useRef(null);
    const navigationNextRef = useRef(null);

    const layoutSettings: LayoutSettings = {
        [BreakpointMinSizes.xl]: {
            slidesPerView: 6
        },
        [BreakpointMinSizes.lg]: {
            slidesPerView: 5
        },
        [BreakpointMinSizes.md]: {
            slidesPerView: 4
        },
        [BreakpointMinSizes.sm]: {
            slidesPerView: 3.5
        },
        [BreakpointMinSizes.xs]: {
            slidesPerView: 2.5
        }
    };

    const ssrLayoutClasses = mapSsrLayoutClasses(layoutSettings);
    const breakPointSettings = getEffectiveLayoutSettings(layoutSettings);

    const updateButtons = () => {
        if (mainSwiper) {
            setAllowPrevious(!mainSwiper.isBeginning);
            setAllowNext(!mainSwiper.isEnd);
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        updateButtons();
    }, [!!mainSwiper]);

    return (
        <div
            className={classNames(
                "d-flex",
                ssrLayoutClasses && ssrLayoutClasses
            )}
        >
            <SwiperNavButton
                className={"category-slider-nav-button d-none d-md-block"}
                innerRef={navigationPrevRef}
                disabled={!allowPrevious}
                icon="left"
                onClick={() => {
                    if (allowPrevious && mainSwiper) {
                        mainSwiper.slidePrev();
                    }
                }}
            />
            <Swiper
                modules={[Navigation]}
                updateOnWindowResize={true}
                navigation={{
                    nextEl: navigationNextRef.current,
                    prevEl: navigationPrevRef.current
                }}
                onSlideChange={() => updateButtons()}
                onSwiper={setMainSwiper}
                slidesPerView={2.5}
                breakpoints={breakPointSettings}
                spaceBetween={0}
            >
                {categories.map((category, index) => (
                    <SwiperSlide
                        key={index}
                        className="categorySlider-slide ssr-slide mt-2"
                    >
                        <div>
                            <a
                                className="d-block"
                                href={category.link}
                                target={category.isTargetBlank ? "_blank" : ""}
                                rel={
                                    category.isTargetBlank
                                        ? "noopener noreferrer"
                                        : ""
                                }
                            >
                                <div className="aspect-ratio-box">
                                    <div className="categorySlider-slide--image-wrapper">
                                        <img
                                            className={classNames(
                                                "mw-100 w-100 h-auto"
                                            )}
                                            alt={category.altText}
                                            src={category.path}
                                            loading="lazy"
                                            decoding="async"
                                            width={120}
                                            height={90}
                                        />
                                    </div>
                                </div>
                                <p className="categorySlider-slide--text text-center mt-2">
                                    {category.altText}
                                </p>
                            </a>
                        </div>
                    </SwiperSlide>
                ))}
            </Swiper>
            <SwiperNavButton
                className={"category-slider-nav-button d-none d-md-block"}
                innerRef={navigationNextRef}
                disabled={!allowNext}
                icon="right"
                onClick={() => {
                    if (allowNext && mainSwiper) {
                        mainSwiper.slideNext();
                    }
                }}
            />
        </div>
    );
};

export default CategorySlider;
